/*
 * @Author: Ryan
 * @Date: 2025-12-29
 * @Description: Hero section for AI Voice Cloning - Sample Voices grid with audio player
 */
import Image from 'next/image';
import React, { useCallback, useRef, useState } from 'react';

import StartButton from './StartButton';

interface VoiceSample {
  id: string;
  name: string;
  avatarUrl: string;
  audioUrl: string;
}

const voiceSamples: VoiceSample[] = [
  {
    id: 'satya',
    name: 'Satya Nadella',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-satya.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-satya.mp3',
  },
  {
    id: 'taylor',
    name: 'Taylor Swift',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-taylor.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-taylor.mp3',
  },
  {
    id: 'michelle',
    name: 'Michelle Obama',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-michelle.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-michelle.mp3',
  },
  {
    id: 'rick',
    name: 'Rick',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-rick.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-rick.mp3',
  },
  {
    id: 'steve',
    name: 'Steve Jobs',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-steve.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-steve.mp3',
  },
  {
    id: 'elsa',
    name: 'Elsa',
    avatarUrl: 'https://assets.falcocut.ai/ai-voice-cloning/avatar-elsa.webp',
    audioUrl: 'https://assets.falcocut.ai/ai-voice-cloning/sample-elsa.mp3',
  },
];

interface VoiceCardProps {
  voice: VoiceSample;
  isPlaying: boolean;
  progress: number;
  onPlay: () => void;
}

const VoiceCard: React.FC<VoiceCardProps> = ({ voice, isPlaying, progress, onPlay }) => {
  return (
    <div className="bg-[#F0F4FF] rounded-2xl p-6 pad:p-8 flex flex-col items-center">
      {/* 头像 */}
      <div className="relative w-[80px] h-[80px] pad:w-[100px] pad:h-[100px] mb-4">
        <Image
          src={voice.avatarUrl}
          alt={voice.name}
          fill
          className="object-cover rounded-full"
        />
      </div>

      {/* 名字 */}
      <h3 className="text-base pad:text-lg font-semibold text-gray-900 mb-4">
        {voice.name}
      </h3>

      {/* 播放器 */}
      <div className="w-full bg-white rounded-full px-2 py-2 flex items-center gap-3 shadow-sm">
        {/* 播放/暂停按钮 */}
        <button
          type="button"
          onClick={onPlay}
          className="flex-shrink-0 w-10 h-10 rounded-full bg-[#4931FA] flex items-center justify-center hover:bg-[#3D28D9] transition-colors"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg width="16" height="18" viewBox="0 0 10 10" fill="none">
              <path
                d="M0 0.999999C0 0.447714 0.447715 0 1 0H8.33333C8.88562 0 9.33333 0.447715 9.33333 1V8.33333C9.33333 8.88562 8.88562 9.33333 8.33333 9.33333H0.999999C0.447714 9.33333 0 8.88562 0 8.33333V0.999999Z"
                fill="white"
              />
            </svg>
          ) : (
            <svg width="16" height="18" viewBox="0 0 10 11" fill="none">
              <path
                d="M8.80097 6.46773C9.694 5.9468 9.694 4.65647 8.80097 4.13553L2.03022 0.185931C1.13024 -0.339061 0 0.310117 0 1.35203V9.25123C0 10.2932 1.13024 10.9423 2.03023 10.4173L8.80097 6.46773Z"
                fill="white"
              />
            </svg>
          )}
        </button>

        {/* 进度条 */}
        <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-[#4931FA] rounded-full transition-all duration-100"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const HeroSection: React.FC = () => {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const animationRef = useRef<number | null>(null);

  // 更新进度
  const updateProgress = useCallback(() => {
    if (playingId && audioRefs.current[playingId]) {
      const audio = audioRefs.current[playingId];
      if (audio && audio.duration) {
        const currentProgress = (audio.currentTime / audio.duration) * 100;
        setProgress((prev) => ({ ...prev, [playingId]: currentProgress }));
      }
      animationRef.current = requestAnimationFrame(updateProgress);
    }
  }, [playingId]);

  // 处理播放/暂停
  const handlePlay = useCallback(
    (voiceId: string, audioUrl: string) => {
      // 如果点击的是当前正在播放的，则暂停
      if (playingId === voiceId) {
        const audio = audioRefs.current[voiceId];
        if (audio) {
          audio.pause();
        }
        setPlayingId(null);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        return;
      }

      // 暂停其他正在播放的音频
      if (playingId && audioRefs.current[playingId]) {
        audioRefs.current[playingId]?.pause();
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      }

      // 创建或获取音频元素
      if (!audioRefs.current[voiceId]) {
        const audio = new Audio(audioUrl);
        audio.addEventListener('ended', () => {
          setPlayingId(null);
          setProgress((prev) => ({ ...prev, [voiceId]: 0 }));
          if (animationRef.current) {
            cancelAnimationFrame(animationRef.current);
          }
        });
        audioRefs.current[voiceId] = audio;
      }

      // 播放新的音频
      const audio = audioRefs.current[voiceId];
      if (audio) {
        audio.play().catch(console.warn);
        setPlayingId(voiceId);
        animationRef.current = requestAnimationFrame(updateProgress);
      }
    },
    [playingId, updateProgress],
  );

  // 清理
  React.useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      Object.values(audioRefs.current).forEach((audio) => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);

  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-12 sm:mb-14 leading-tight">
          Sample Voices
        </h2>

        {/* 音频卡片网格 */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6 mb-10 pad:mb-12 sm:mb-14">
          {voiceSamples.map((voice) => (
            <VoiceCard
              key={voice.id}
              voice={voice}
              isPlaying={playingId === voice.id}
              progress={progress[voice.id] || 0}
              onPlay={() => handlePlay(voice.id, voice.audioUrl)}
            />
          ))}
        </div>

        {/* CTA 按钮 */}
        <div className="flex justify-center">
          <StartButton text="Try Voice Cloning" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
